import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import styled from 'styled-components'

import type {
  StatusChangeReasonGroupType,
  StatusChangeReasonType,
} from 'shared/common/global-types.d'
import { StatusEnumType } from 'shared/common/global-types.runtime'
import { SIZES } from '~/common/globalStyles'
import Button, { LoadingButton } from '~/views/components/ui/Button'
import Icon from '~/views/components/ui/Icon'
import IconButton from '~/views/components/ui/IconButton'
import TextField from '~/views/components/ui/inputs/TextField'
import Select, { Option } from '~/views/components/ui/inputs/v2/Select'

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${SIZES.xl};
`

const TitleSubtitle = styled.div`
  font-size: ${SIZES.large};
`

const ReasonForm = styled(DialogContent)`
  display: grid;
  grid-gap: ${SIZES.medium};
  grid-template-rows: auto;

  && {
    padding-bottom: ${SIZES.xl};
    padding-top: 0px;
    overflow: initial;
  }
`

const StatusReasonDialog = styled(Dialog)`
  & .MuiDialog-paper {
    max-height: 95vh;
    min-width: 37vh;
    max-width: 90vh;
    width: 490px;
    height: 560px;
    min-height: 32vh;
  }

  & .MuiDialogContent-root {
    padding: 8px 16px 0px;
  }
`

const ReasonDialogActions = styled(DialogActions)(
  ({ theme }) => `
    width: 30%;
    display: grid;
    grid-template-columns: auto 113px;
    grid-gap: ${theme.spacing(2)};
    margin-left: auto;
    padding: ${theme.spacing(2)}; ${theme.spacing(2)};`,
)

const ReasonDialogTitle = styled(DialogTitle)`
  padding-bottom: 0;
`

interface ReasonDialogProps {
  title: string
  subTitle: JSX.Element
  status: StatusEnumType | null | undefined
  open: boolean
  loading: boolean
  disabled: boolean
  onClose(): void
  onSave(): Promise<void>
  setSelectedGroupId(value: React.SetStateAction<string>): void
  setSelectedReasonId(value: React.SetStateAction<string>): void
  setDescription(value: React.SetStateAction<string>): void
  reasonGroups?: StatusChangeReasonGroupType[]
  selectedGroupId?: string
  selectedReasonId?: string
  description?: string | null
  reasonGroup?: StatusChangeReasonGroupType | null
  reason?: StatusChangeReasonType
  isAddReason?: boolean
}

const ReasonDialog = ({
  disabled,
  description,
  loading,
  isAddReason,
  onClose,
  onSave,
  open,
  reason,
  reasonGroup,
  reasonGroups,
  selectedGroupId,
  selectedReasonId,
  setDescription,
  setSelectedGroupId,
  setSelectedReasonId,
  subTitle,
  title,
}: ReasonDialogProps) => {
  const onSubmit = async () => {
    await onSave()
    updateState()
  }

  const getGroupId = () => {
    if (selectedGroupId === reasonGroup?.id) {
      return selectedGroupId
    } else if (selectedGroupId && selectedGroupId !== reasonGroup?.id) {
      return selectedGroupId
    } else {
      return reasonGroup?.id
    }
  }

  const getReasonId = () => {
    if (selectedReasonId === reason?.id) {
      return selectedReasonId
    } else if (selectedReasonId && selectedReasonId !== reason?.id) {
      return selectedReasonId
    } else {
      return reason?.id
    }
  }

  const updateState = () => {
    if (isAddReason) {
      setDescription('')
      setSelectedGroupId('')
      setSelectedReasonId('')
    } else {
      setDescription(description || '')
      setSelectedGroupId(reasonGroup?.id || '')
      setSelectedReasonId(reason?.id || '')
    }
  }

  const onCloseReasonDialog = () => {
    updateState()
    onClose()
  }

  return (
    <StatusReasonDialog
      open={open}
      onClick={(event) => {
        event.stopPropagation()
        event.preventDefault()
      }}
      onClose={() => onCloseReasonDialog()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      data-testid="alert-dialog"
      disableEnforceFocus
    >
      <ReasonDialogTitle>
        <IconButton
          position="right"
          onClick={() => onCloseReasonDialog()}
          size="large"
        >
          <Icon data-testid="reasonDialogCloseButton" name="cancel" />
        </IconButton>
      </ReasonDialogTitle>
      <ReasonForm>
        <TitleContainer>{title}</TitleContainer>
        <TitleSubtitle>{subTitle}</TitleSubtitle>
        <Select
          fullWidth
          label="Group"
          name="group"
          onChange={(value: string) => {
            setSelectedGroupId(value)
          }}
          placeholder="Select Group..."
          required
          searchable
          value={getGroupId()}
        >
          {reasonGroups?.map((reasonGroup: StatusChangeReasonGroupType) => (
            <Option value={reasonGroup.id} key={reasonGroup.id}>
              {reasonGroup.name}
            </Option>
          ))}
        </Select>
        <Select
          dataTestId="reasonSelect"
          disabled={!selectedGroupId && !reasonGroup?.id}
          fullWidth
          required
          label="Reason"
          name="reason"
          onChange={(value: string) => {
            setSelectedReasonId(value)
          }}
          placeholder="Select Reason..."
          value={getReasonId()}
        >
          {reasonGroups
            ?.find(
              (selectedReasonGroup: StatusChangeReasonGroupType) =>
                getGroupId() === selectedReasonGroup.id,
            )
            ?.statusChangeReasons?.slice()
            .sort((firstChangeReason, secondChangeReason) =>
              firstChangeReason.name > secondChangeReason.name ? 1 : -1,
            )
            .map((reason: StatusChangeReasonType) => (
              <Option value={reason.id} key={reason.id}>
                {reason.name}
              </Option>
            ))}
        </Select>
        <TextField
          fullWidth
          multiline
          label="Description"
          name="description"
          placeholder="Why was the status changed?"
          rows={6}
          value={description || ''}
          maxLength={1000}
          onChange={(event) => setDescription(event.target.value)}
        />
      </ReasonForm>
      <ReasonDialogActions>
        <Button
          bordered
          large
          name="cancel"
          onClick={() => onCloseReasonDialog()}
          size="large"
        >
          Cancel
        </Button>
        <LoadingButton
          buttonText="Save Reason"
          disabled={disabled}
          large
          loading={loading}
          name="Save Reason"
          onClick={onSubmit}
          raised
          size="large"
        />
      </ReasonDialogActions>
    </StatusReasonDialog>
  )
}

export default ReasonDialog
